import { Component, OnDestroy, OnInit } from '@angular/core';
import { RootMixin, UserAwareMixin, WithMenubarMixin } from '@fapp/mixins';
import { combineLatest, Subject, takeUntil } from 'rxjs';

import { PermissionsService } from '../../../core/services/permissions.service';
import {
  MenuItem,
  SidebarColorConfig,
} from '../../partials/sidebar/sidebar.component';

@Component({
  selector: 'app-staff-layout',
  templateUrl: './staff-layout.component.html',
  styleUrls: ['./staff-layout.component.scss'],
  standalone: false,
})
export class StaffLayoutComponent
  extends WithMenubarMixin(UserAwareMixin(RootMixin))
  implements OnInit, OnDestroy
{
  sidebarCollapsed = false;
  filteredMenuItems: MenuItem[] = [];
  private destroy$ = new Subject<void>();

  // Custom color configuration using info theme
  sidebarColors: SidebarColorConfig = {
    background: 'var(--m4p-info-50)',
    submenuBackground: 'var(--m4p-info-100)',
    textColor: 'var(--m4p-secondary-700)',
    textHoverColor: 'var(--m4p-secondary-800)',
    activeTextColor: 'var(--m4p-info-700)',
    hoverBackground: 'var(--m4p-info-100)',
    activeBackground: 'var(--m4p-info-200)',
    submenuActiveBackground: 'var(--m4p-info-300)',
    borderColor: 'var(--m4p-info-200)',
    activeBorderColor: 'var(--m4p-info-500)',
    toggleColor: 'var(--m4p-secondary-600)',
    toggleHoverBackground: 'var(--m4p-info-100)',
  };

  allMenuItems: MenuItem[] = [
    {
      label: 'Companies',
      icon: 'mat::company',
      route: '/companies',
      children: [
        {
          label: 'All Companies',
          icon: 'mat::categories',
          route: '/companies',
        },
        {
          label: 'Add Company',
          icon: 'mat::add_circle',
          route: '/companies/add',
          requireSuperuser: true,
        },
      ],
    },
    {
      label: 'Users',
      icon: 'mat::groups',
      route: '/users',
      children: [
        {
          label: 'All Users',
          icon: 'mat::groups',
          route: '/users',
        },
        {
          label: 'Add User',
          icon: 'mat::add_circle',
          route: '/users/add',
        },
      ],
    },
    {
      label: 'Stock Counts',
      icon: 'mat::fact_check',
      route: '/stock-counts',
      children: [
        {
          label: 'New Stock Count',
          icon: 'mat::add_circle',
          route: '/stock-counts/new',
        },
        {
          label: 'All Stock Counts',
          icon: 'mat::list',
          route: '/stock-counts',
        },
      ],
    },
    {
      label: 'SAP Query',
      icon: 'mat::search',
      route: '/sap-query',
      requireSuperuser: true,
    },
    {
      label: 'SAP Configuration',
      icon: 'mat::build',
      route: '/odata-query-generator',
      requireSuperuser: true,
    },
    {
      label: 'Config Company',
      icon: 'mat::settings',
      route: '/config-company',
      requireSuperuser: true,
    },
    {
      label: 'My Profile',
      icon: 'mat::account_box',
      route: '/users/profile',
      children: [
        {
          label: 'Profile Settings',
          icon: 'mat::person',
          route: '/users/profile',
        },
        {
          label: 'My Company',
          icon: 'mat::business',
          route: '/company',
          requiredGroups: ['Manager'],
        },
      ],
    },
  ];

  // Flag to determine whether to use dynamic or static menu
  private useDynamicMenu = true;

  constructor(private permissionsService: PermissionsService) {
    super();
  }

  ngOnInit(): void {
    if (this.useDynamicMenu) {
      // Load dynamic menu from backend
      combineLatest([
        this.currentUser$,
        this.permissionsService.loadSidebarMenu(),
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([user, dynamicMenu]) => {
          console.log(
            'StaffLayoutComponent - Using dynamic menu:',
            dynamicMenu
          );

          // If dynamic menu is available, use it; otherwise fall back to static menu
          if (dynamicMenu && dynamicMenu.length > 0) {
            this.filteredMenuItems = dynamicMenu;
          } else {
            console.log('StaffLayoutComponent - Falling back to static menu');
            this.filteredMenuItems = this.filterMenuItemsByUserPermissions(
              this.allMenuItems,
              user
            );
          }
        });
    } else {
      // Use static menu with client-side filtering (original behavior)
      this.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        console.log('StaffLayoutComponent - Current user data:', user);
        console.log(
          'StaffLayoutComponent - User isSuperuser:',
          user?.isSuperuser
        );
        this.filteredMenuItems = this.filterMenuItemsByUserPermissions(
          this.allMenuItems,
          user
        );
        console.log(
          'StaffLayoutComponent - Filtered menu items:',
          this.filteredMenuItems
        );
        console.log(
          'StaffLayoutComponent - SAP Query item found:',
          this.filteredMenuItems.find(item => item.label === 'SAP Query')
        );
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterMenuItemsByUserPermissions(
    menuItems: MenuItem[],
    user: any
  ): MenuItem[] {
    if (!user) {
      return menuItems.filter(
        item => !item.requiredGroups && !item.requireSuperuser
      );
    }

    return menuItems
      .filter(item => {
        // Check superuser requirement
        if (item.requireSuperuser && !user.isSuperuser) {
          return false;
        }

        // Check group requirements
        if (item.requiredGroups && item.requiredGroups.length > 0) {
          const userGroupNames =
            user.groups?.map((group: any) => group.name) || [];
          if (
            !item.requiredGroups.some(requiredGroup =>
              userGroupNames.includes(requiredGroup)
            )
          ) {
            return false;
          }
        }

        return true;
      })
      .map(item => ({
        ...item,
        // Also filter children if they exist
        children: item.children
          ? this.filterMenuItemsByUserPermissions(item.children, user)
          : item.children,
      }));
  }

  // Getter for template to use the filtered menu items
  get menuItems(): MenuItem[] {
    return this.filteredMenuItems;
  }
}
